import { StyleProp, StyleSheet, TextStyle } from 'react-native';

let defaultFontFamily: string | undefined;
const mbTextStylesSetDefaultFontFamily = (fontFamily: string) => {
    defaultFontFamily = fontFamily;
};

interface MB_TextStylesOptions {
    allowLineHeight?: boolean,
    includeFontPadding?: boolean,
}

type AcceptedStyles = (TextStyle | StyleProp<TextStyle> | number);

type FontWeights = 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | undefined;

/**
 * Transform styles associated with TextStyles. Does the following
 * Takes in a fontFamily(or uses the default), fontWeight and fontStyle and combines them to get a new fontFamily
 * in format of <fontName>_<fontWeight>_<fontStyle> and gets rid of fontWeight and fontStyle.
 * Raises a warning when lineHeight is used. if allowLineHeight is not passed. Line height can be problematic with some fonts and also act
 * differently on different platforms. It`s much better to use padding and margin to control the spacing if possible.
 * 
 * IMPORTANT NOTE: This function expects that whatever font will be used is in format of <fontName>_<fontWeight>_<fontStyle>.
 * Here fontName is the base name of any font you want to use, fontWeight is one of the allowed values in styles fontWeight prop (100, 200, ...900, normal, bold),
 * and fontStyle should either be empty(in which case also remove the leading underline) or it`s _italic.
 *
 * For example, your fonts can have the following names. Poppins_300, Poppins_300_italic, Poppins_100 and so on.
 * Note that the function does not do any checking to ensure that the font exists. It just assumes that
 * font exists for all 100-900 plus normal and bold weights and also assumes that there is a _italic variation for each weight.
 *
 * A very common usage of mbTextStyles is to declare StyleSheet properties, in which case your code can look something like this.
 *
 * const styles = StyleSheet.create({
 *     someTextStyle: mbTextStyles({
 *         fontFamily: 'Poppins',
 *         fontWeight: '500,
 *         .......
 *     }),
 *
 *     // Note that in this one textStyles.normalText is a memoized value from a predefined StyleSheet.create instance.
 *     // We will use StyleSheet.flatten and extract all of the values from textStyles.normalText,
 *     someTextWithPredefinedStyle: mbTextStyles([
 *         textStyles.normalText, {
 *             fontWeight: 'bold',
 *         },
 *     ]),
 * })
 *
 *
 * @param styles expects a TextStyle, StyleProp<TextStyle> or a number corresponding to memoized style.
 * @returns TextStyle with all fontFamily, fontWeight, fontStyle modifications applied.
 */
const mbTextStyles = (styles: AcceptedStyles | (AcceptedStyles)[], options?: MB_TextStylesOptions): TextStyle => {
    const stylesFlattened = StyleSheet.flatten(styles);

    if (stylesFlattened === undefined || (typeof stylesFlattened === 'object' && Object.keys(stylesFlattened as Object).length === 0)) {
        console.warn('Warning: Passing empty style to mbTextStyles is not recommended and will be deprecated in upcoming versions.');
    }

    const { fontFamily, lineHeight, ...restOfStyles } = stylesFlattened as TextStyle;
    let { fontStyle: fontStyleInternal, fontWeight: fontWeightInternal } = stylesFlattened as TextStyle;

    if (!options?.allowLineHeight && lineHeight) {
        console.warn('Warning: lineHeight might introduce issues for mobile devices. Consider using something else instead of lineHeight.');
    }

    let modifiedFontFamily: string | undefined = fontFamily ?? defaultFontFamily;

    if (!modifiedFontFamily) {
        console.error('Error: In order to use mbTextStyles, you must either provide a font family or set the default font family. Returning empty style');
        return {};
    }

    // In case if passed font is already in <fontName>_<fontWeight>_<fontStyle> format(For example Poppins_300_italic),
    // we will deconstruct it and will set the correct base fontName, fontWeight and fontStyle.
    if (modifiedFontFamily.indexOf('_') !== -1) {
        const modifiedFontFamilyDeconstructed = modifiedFontFamily.split('_');
        const family = modifiedFontFamilyDeconstructed[0];
        let weight: string | undefined = modifiedFontFamilyDeconstructed[1];
        let style: string | undefined = modifiedFontFamilyDeconstructed[2];
        if (style !== 'normal' && style !== 'italic' && style !== undefined) {
            style = undefined;
        }
        if (weight !== 'normal' && weight !== 'bold' && weight !== '100' && weight !== '200' && weight !== '300' && weight !== '400' && weight !== '500' &&
            weight !== '600' && weight !== '700' && weight !== '800' && weight !== '900') {
            weight = undefined;
        }

        modifiedFontFamily = family;
        fontStyleInternal = fontStyleInternal ?? (style as 'normal' | 'italic' | undefined); // bit build was throwing erro without this type conversion
        fontWeightInternal = fontWeightInternal ?? (weight as FontWeights); // bit build was throwing erro without this type conversion
    }

    // Modify fontFamily with the correct font weight. Either use the provided weight or use default value of 500.
    if (fontWeightInternal) {
        if (fontWeightInternal === 'normal') {
            modifiedFontFamily += '_400';
        } else if (fontWeightInternal === 'bold') {
            modifiedFontFamily += '_700';
        } else {
            modifiedFontFamily += `_${fontWeightInternal}`;
        }
    } else {
        modifiedFontFamily += '_400';
    }

    // Modify font family with the correct italic.
    if (fontStyleInternal === 'italic') {
        modifiedFontFamily += '_italic';
    }

    const modifiedTextStyle: TextStyle = {
        fontFamily: modifiedFontFamily,
        lineHeight,
        includeFontPadding: options?.includeFontPadding ?? false,
        ...restOfStyles,
    };

    return modifiedTextStyle;
};

export { mbTextStyles, mbTextStylesSetDefaultFontFamily };
